import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  User, Mail, Calendar, Shield, Gift, Copy, Check, QrCode, Key, Globe, Bell, BellOff,
  Building2, CreditCard, Lock, CheckCircle, AlertCircle, Trash2, RefreshCw, Sun, Moon, Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import NotificationSettings from '../components/NotificationSettings';
import { WeeklyUpdateToggle } from '../components/sblive/WeeklyUpdateToggle';

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // 2FA State
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  // Currency State
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(user?.preferred_currency || 'USD');
  const [savingCurrency, setSavingCurrency] = useState(false);

  // Push Notification State
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Banking State
  const [bankAccount, setBankAccount] = useState(null);
  const [showBankSetup, setShowBankSetup] = useState(false);
  const [bankForm, setBankForm] = useState({
    routing_number: '',
    account_number: '',
    account_type: 'checking',
    account_holder_name: ''
  });
  const [savingBank, setSavingBank] = useState(false);
  const [showVerifyBank, setShowVerifyBank] = useState(false);
  const [verifyAmounts, setVerifyAmounts] = useState({ amount1: '', amount2: '' });
  const [verifyingBank, setVerifyingBank] = useState(false);

  // Delete Account State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const { logout } = useAuth();

  useEffect(() => {
    fetchData();
    checkPushSubscription();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, userRes, currencyRes, bankRes] = await Promise.all([
        api.get('/referral/stats'),
        api.get('/auth/me'),
        api.get('/currency/supported'),
        api.get('/banking/account')
      ]);
      setReferralStats(statsRes.data);
      updateUser(userRes.data);
      setCurrencies(currencyRes.data.currencies);
      setSelectedCurrency(userRes.data.preferred_currency || 'USD');
      setBankAccount(bankRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkPushSubscription = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setPushEnabled(!!subscription);
      } catch (error) {
        console.error('Error checking push subscription:', error);
      }
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${referralStats?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCurrencyChange = async (currencyCode) => {
    setSavingCurrency(true);
    try {
      await api.post(`/currency/set-preference?currency=${currencyCode}`);
      setSelectedCurrency(currencyCode);
      const userRes = await api.get('/auth/me');
      updateUser(userRes.data);
      toast.success(`Currency changed to ${currencyCode}`);
    } catch (error) {
      toast.error('Failed to update currency preference');
    } finally {
      setSavingCurrency(false);
    }
  };

  const togglePushNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    setPushLoading(true);
    try {
      if (pushEnabled) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          await api.delete('/push/unsubscribe');
        }
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      } else {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('Notification permission denied');
          setPushLoading(false);
          return;
        }

        const vapidRes = await api.get('/push/vapid-public-key');
        const vapidPublicKey = vapidRes.data.publicKey;

        let registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
          registration = await navigator.serviceWorker.register('/sw.js');
        }
        await navigator.serviceWorker.ready;

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });

        const subscriptionJSON = subscription.toJSON();
        await api.post('/push/subscribe', {
          endpoint: subscriptionJSON.endpoint,
          keys: subscriptionJSON.keys
        });

        setPushEnabled(true);
        toast.success('Push notifications enabled!');
      }
    } catch (error) {
      console.error('Push notification error:', error);
      toast.error('Failed to toggle push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Banking functions
  const handleBankSetup = async () => {
    if (!bankForm.routing_number || !bankForm.account_number || !bankForm.account_holder_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (bankForm.routing_number.length !== 9) {
      toast.error('Routing number must be 9 digits');
      return;
    }

    if (bankForm.account_number.length < 8) {
      toast.error('Account number must be at least 8 digits');
      return;
    }

    setSavingBank(true);
    try {
      await api.post('/banking/setup', bankForm);
      toast.success('Bank account added successfully!');
      setShowBankSetup(false);
      setBankForm({ routing_number: '', account_number: '', account_type: 'checking', account_holder_name: '' });
      const bankRes = await api.get('/banking/account');
      setBankAccount(bankRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bank account');
    } finally {
      setSavingBank(false);
    }
  };

  const handleDeleteBank = async () => {
    if (!window.confirm('Are you sure you want to remove your bank account?')) return;
    
    try {
      await api.delete('/banking/account');
      toast.success('Bank account removed');
      setBankAccount({ has_bank_account: false });
    } catch (error) {
      toast.error('Failed to remove bank account');
    }
  };

  const initiateVerification = async () => {
    try {
      const res = await api.post('/banking/verify');
      toast.success(res.data.message);
      setShowVerifyBank(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate verification');
    }
  };

  const confirmVerification = async () => {
    if (!verifyAmounts.amount1 || !verifyAmounts.amount2) {
      toast.error('Please enter both deposit amounts');
      return;
    }

    setVerifyingBank(true);
    try {
      const res = await api.post(`/banking/verify/confirm?amount1=${verifyAmounts.amount1}&amount2=${verifyAmounts.amount2}`);
      if (res.data.verified) {
        toast.success('Bank account verified!');
        setShowVerifyBank(false);
        const bankRes = await api.get('/banking/account');
        setBankAccount(bankRes.data);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyingBank(false);
    }
  };

  // 2FA functions
  const setup2FA = async () => {
    try {
      const res = await api.post('/2fa/setup');
      setTwoFAData(res.data);
      setShow2FASetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    }
  };

  const verify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      await api.post('/2fa/verify', { code: verifyCode });
      toast.success('2FA enabled successfully!');
      setShow2FASetup(false);
      setTwoFAData(null);
      setVerifyCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifying(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setDisabling2FA(true);
    try {
      await api.post('/2fa/disable', { code: disableCode });
      toast.success('2FA disabled');
      setDisableCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setDisabling2FA(false);
    }
  };

  // Delete Account
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Please enter your password to confirm');
      return;
    }

    setDeletingAccount(true);
    try {
      // Verify password first by trying to login
      await api.post('/auth/login', { email: user.email, password: deletePassword });
      
      // If password is correct, proceed with deletion
      await api.delete('/auth/delete-account');
      toast.success('Your account has been permanently deleted');
      setShowDeleteModal(false);
      
      // Log out and redirect
      await logout();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Incorrect password');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete account');
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Settings
          </h1>
          <p className="text-gray-400 mt-1">Manage your account, banking, and preferences</p>
        </div>

        <WeeklyUpdateToggle />

        {/* Profile Card */}
        <Card className="card-dashboard">
          <CardHeader><CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Profile</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <p className="font-semibold text-white text-lg">{user?.name}</p>
                <p className="text-gray-400">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Banking Setup Card */}
        <Card className="card-dashboard border-[#00E676]/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Building2 className="w-5 h-5 text-[#00E676]" />
              Bank Account for Withdrawals
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {bankAccount?.has_bank_account ? (
              <>
                <div className={`p-4 rounded-xl border ${bankAccount.verified ? 'bg-[#00E676]/10 border-[#00E676]/20' : 'bg-yellow-500/10 border-yellow-500/20'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${bankAccount.verified ? 'bg-[#00E676]/20' : 'bg-yellow-500/20'}`}>
                        {bankAccount.verified ? (
                          <CheckCircle className="w-6 h-6 text-[#00E676]" />
                        ) : (
                          <AlertCircle className="w-6 h-6 text-yellow-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{bankAccount.account_holder_name}</p>
                        <p className="text-sm text-gray-400 capitalize">{bankAccount.account_type} Account</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-400">Account</p>
                      <p className="font-mono text-white">****{bankAccount.account_number_last4}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Routing Number</p>
                      <p className="font-mono text-gray-300">*****{bankAccount.routing_number_last4}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${bankAccount.verified ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-yellow-500/20 text-yellow-500'}`}>
                      {bankAccount.verified ? 'Verified' : 'Pending Verification'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3">
                  {!bankAccount.verified && (
                    <Button onClick={initiateVerification} className="btn-primary flex-1" data-testid="verify-bank-btn">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Verify Account
                    </Button>
                  )}
                  <Button onClick={() => setShowBankSetup(true)} variant="outline" className="btn-secondary">
                    Update
                  </Button>
                  <Button onClick={handleDeleteBank} variant="outline" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/20">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-400">
                  Add your bank account to receive withdrawals directly. Your information is encrypted and secure.
                </p>
                <Button onClick={() => setShowBankSetup(true)} className="btn-primary w-full" data-testid="add-bank-btn">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add Bank Account
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Theme Toggle */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isDark ? <Moon className="w-5 h-5 text-[#6366F1]" /> : <Sun className="w-5 h-5 text-[#F7931A]" />}
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-400">Choose your preferred theme for the application.</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => !isDark && toggleTheme()}
                className={`p-4 rounded-xl border transition-all flex items-center gap-3 ${
                  isDark
                    ? 'bg-[#6366F1]/20 border-[#6366F1] text-white'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                }`}
                data-testid="theme-dark"
              >
                <Moon className="w-6 h-6" />
                <div className="text-left">
                  <p className="font-medium">Dark Mode</p>
                  <p className="text-xs opacity-70">Easy on the eyes</p>
                </div>
              </button>
              <button
                onClick={() => isDark && toggleTheme()}
                className={`p-4 rounded-xl border transition-all flex items-center gap-3 ${
                  !isDark
                    ? 'bg-[#F7931A]/20 border-[#F7931A] text-white'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                }`}
                data-testid="theme-light"
              >
                <Sun className="w-6 h-6" />
                <div className="text-left">
                  <p className="font-medium">Light Mode</p>
                  <p className="text-xs opacity-70">Bright and clear</p>
                </div>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Currency Preferences */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Globe className="w-5 h-5 text-[#00E676]" />
              Currency Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-400">Select your preferred currency for displaying balances and prices.</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {currencies.map((currency) => (
                <button
                  key={currency.code}
                  onClick={() => handleCurrencyChange(currency.code)}
                  disabled={savingCurrency}
                  className={`p-3 rounded-xl border transition-all ${
                    selectedCurrency === currency.code
                      ? 'bg-[#00E676]/20 border-[#00E676] text-white'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                  }`}
                  data-testid={`currency-${currency.code}`}
                >
                  <div className="text-lg font-bold">{currency.symbol}</div>
                  <div className="text-xs">{currency.code}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications - Enhanced */}
        <NotificationSettings />

        {/* Two-Factor Authentication */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Key className="w-5 h-5 text-[#6366F1]" />
              Two-Factor Authentication
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`p-4 rounded-xl border ${user?.two_fa_enabled ? 'bg-[#00E676]/10 border-[#00E676]/20' : 'bg-white/5 border-white/10'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">{user?.two_fa_enabled ? '2FA is Enabled' : '2FA is Disabled'}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {user?.two_fa_enabled ? 'Your account is protected with an authenticator app' : 'Add an extra layer of security'}
                  </p>
                </div>
                {user?.two_fa_enabled ? <Check className="w-6 h-6 text-[#00E676]" /> : <Shield className="w-6 h-6 text-gray-500" />}
              </div>
            </div>

            {user?.two_fa_enabled ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-400">Enter your authenticator code to disable 2FA:</p>
                <div className="flex gap-3">
                  <Input type="text" value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="input-dark text-center font-mono text-lg tracking-widest" maxLength={6} data-testid="disable-2fa-input" />
                  <Button onClick={disable2FA} disabled={disabling2FA} className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/20" data-testid="disable-2fa-btn">
                    {disabling2FA ? 'Disabling...' : 'Disable 2FA'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={setup2FA} className="btn-primary" data-testid="enable-2fa-btn">
                <Shield className="w-4 h-4 mr-2" />
                Enable 2FA
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Referral Program */}
        <Card className="card-dashboard border-[#6366F1]/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Gift className="w-5 h-5 text-[#6366F1]" />
              Referral Program
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-400">Invite friends and earn <span className="text-[#00E676] font-semibold">3 trades with 20% fee discount</span>!</p>
            <div className="p-4 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20">
              <p className="text-sm text-gray-400 mb-2">Your Referral Code</p>
              <div className="flex items-center gap-3">
                <code className="text-2xl font-bold text-white tracking-wider">{referralStats?.referral_code}</code>
                <Button onClick={copyReferralLink} variant="outline" size="sm" className="btn-secondary" data-testid="copy-referral-btn">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-xs text-gray-500">Total Referrals</p>
                <p className="text-2xl font-bold text-white">{referralStats?.total_referrals || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-xs text-gray-500">Discount Trades Left</p>
                <p className="text-2xl font-bold text-[#00E676]">{referralStats?.discount_trades_remaining || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delete Account Card */}
        <Card className="card-dashboard border-red-500/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">
                <strong>Warning:</strong> Deleting your account is permanent and cannot be undone. All your data, including your portfolio, transaction history, and referral bonuses will be permanently removed.
              </p>
            </div>
            <Button 
              onClick={() => setShowDeleteModal(true)} 
              className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/20"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete My Account
            </Button>
          </CardContent>
        </Card>

        {/* Bank Setup Dialog */}
        <Dialog open={showBankSetup} onOpenChange={setShowBankSetup}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Building2 className="w-5 h-5 text-[#00E676]" />
                {bankAccount?.has_bank_account ? 'Update Bank Account' : 'Add Bank Account'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-[#00E676]/10 border border-[#00E676]/20 flex items-start gap-2">
                <Lock className="w-4 h-4 text-[#00E676] mt-0.5" />
                <p className="text-xs text-[#00E676]">Your bank information is encrypted and stored securely. We only show the last 4 digits.</p>
              </div>
              
              <div className="space-y-2">
                <Label>Account Holder Name *</Label>
                <Input value={bankForm.account_holder_name} onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})} placeholder="John Doe" className="input-dark" data-testid="bank-holder-name" />
              </div>

              <div className="space-y-2">
                <Label>Routing Number * (9 digits)</Label>
                <Input value={bankForm.routing_number} onChange={(e) => setBankForm({...bankForm, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9)})} placeholder="123456789" className="input-dark font-mono" maxLength={9} data-testid="bank-routing" />
              </div>

              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input value={bankForm.account_number} onChange={(e) => setBankForm({...bankForm, account_number: e.target.value.replace(/\D/g, '').slice(0, 17)})} placeholder="1234567890" className="input-dark font-mono" type="password" data-testid="bank-account" />
              </div>

              <div className="space-y-2">
                <Label>Account Type</Label>
                <Select value={bankForm.account_type} onValueChange={(value) => setBankForm({...bankForm, account_type: value})}>
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="checking">Checking</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBankSetup(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleBankSetup} disabled={savingBank} className="btn-primary" data-testid="save-bank-btn">
                {savingBank ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {savingBank ? 'Saving...' : 'Save Bank Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bank Verification Dialog */}
        <Dialog open={showVerifyBank} onOpenChange={setShowVerifyBank}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <CheckCircle className="w-5 h-5 text-[#00E676]" />
                Verify Bank Account
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-gray-400">
                We sent two small deposits to your bank account. Enter the exact amounts below to verify ownership.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Deposit ($)</Label>
                  <Input type="number" step="0.01" value={verifyAmounts.amount1} onChange={(e) => setVerifyAmounts({...verifyAmounts, amount1: e.target.value})} placeholder="0.00" className="input-dark" data-testid="verify-amount1" />
                </div>
                <div className="space-y-2">
                  <Label>Second Deposit ($)</Label>
                  <Input type="number" step="0.01" value={verifyAmounts.amount2} onChange={(e) => setVerifyAmounts({...verifyAmounts, amount2: e.target.value})} placeholder="0.00" className="input-dark" data-testid="verify-amount2" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVerifyBank(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={confirmVerification} disabled={verifyingBank} className="btn-primary" data-testid="confirm-verify-btn">
                {verifyingBank ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {verifyingBank ? 'Verifying...' : 'Verify Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 2FA Setup Dialog */}
        <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-[#6366F1]" />
                  Setup Two-Factor Authentication
                </div>
              </DialogTitle>
            </DialogHeader>
            {twoFAData && (
              <div className="space-y-6 py-4">
                <div className="text-center">
                  <p className="text-gray-400 mb-4">Scan this QR code with your authenticator app:</p>
                  <img src={twoFAData.qr_code} alt="2FA QR Code" className="mx-auto rounded-xl border border-white/10" />
                </div>
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-xs text-gray-500 mb-2">Manual Entry Key</p>
                  <code className="text-sm text-[#00E676] break-all">{twoFAData.secret}</code>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-400">Enter the 6-digit code from your app:</p>
                  <Input type="text" value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className="input-dark text-center font-mono text-xl tracking-widest" maxLength={6} data-testid="verify-2fa-input" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShow2FASetup(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={verify2FA} disabled={verifying} className="btn-primary" data-testid="verify-2fa-btn">
                {verifying ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Account Confirmation Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Trash2 className="w-5 h-5" />
                Delete Account Permanently
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 text-sm font-medium mb-2">This action cannot be undone!</p>
                <p className="text-gray-400 text-sm">
                  All your data will be permanently deleted including:
                </p>
                <ul className="text-gray-400 text-sm mt-2 space-y-1 list-disc list-inside">
                  <li>Account information</li>
                  <li>Portfolio and positions</li>
                  <li>Transaction history</li>
                  <li>Referral bonuses and rewards</li>
                  <li>Cashback earnings</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Enter your password to confirm:</Label>
                <Input 
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your password"
                  className="input-dark"
                  data-testid="delete-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => { setShowDeleteModal(false); setDeletePassword(''); }} 
                className="btn-secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteAccount} 
                disabled={deletingAccount || !deletePassword}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-delete-btn"
              >
                {deletingAccount ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {deletingAccount ? 'Deleting...' : 'Delete My Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
